export type DocumentStatus = 'uploading' | 'processing' | 'processing_claimed' | 'uploaded' | 'failed' | 'invalid' | 'queued' | 'sent';

export type CustomsDocument = {
  documentId: string;
  documentType: string;
  fileName: string;
  status: DocumentStatus;
  error?: string | null;
  externalId?: string | null;
};

/**
 * Raw document shape from the API -- casing is inconsistent (camelCase, PascalCase, snake_case).
 */
export type RawApiDocument = {
  documentId?: string;
  DocumentId?: string;
  document_id?: string;
  documentType?: string;
  Type?: string;
  type?: string;
  document_type?: string;
  fileName?: string;
  FileName?: string;
  file_name?: string;
  status?: string;
  Status?: string;
  error?: string | null;
  Error?: string | null;
  externalId?: string | null;
  ExternalId?: string | null;
  external_id?: string | null;
};

// ─── State Types ──────────────────────────────────────────────────────────────

type OrderDocumentState = {
  documents: CustomsDocument[];
  isLoading: boolean;
  isUploading: boolean;
  uploadProgress: number;
  error: unknown;
};

type ShipmentDocumentState = {
  documents: CustomsDocument[];
  isLoading: boolean;
  isUploading: boolean;
  isSending: boolean;
  error: unknown;
};

export type CustomsDocumentsState = {
  capabilities: { carriers: Array<{ carrierId: string }>; document_types?: string[]; documentTypes?: string[] } | null;
  isLoadingCapabilities: boolean;
  capabilitiesError: unknown;
  orderDocuments: Record<string, OrderDocumentState>;
  shipmentDocuments: Record<string, ShipmentDocumentState>;
  pendingDocuments: Record<string, boolean>;
};

export type CustomsDocumentsRootState = {
  customsDocuments: CustomsDocumentsState;
};

// ─── API Types ────────────────────────────────────────────────────────────────

export type UploadApiError = {
  response?: {
    data?: {
      Errors?: Array<{ Message?: string }>;
      message?: string;
    };
  };
  message?: string;
};

export const normalizeDocument = (doc: RawApiDocument): CustomsDocument => ({
  documentId: (doc.documentId ?? doc.DocumentId ?? doc.document_id) || '',
  documentType: (doc.documentType ?? doc.Type ?? doc.type ?? doc.document_type) || '',
  fileName: (doc.fileName ?? doc.FileName ?? doc.file_name) || '',
  status: (doc.status ?? doc.Status ?? '').toLowerCase() as DocumentStatus,
  error: doc.error ?? doc.Error ?? null,
  externalId: doc.externalId ?? doc.ExternalId ?? doc.external_id ?? null,
});

export const normalizeDocuments = (documents: unknown): CustomsDocument[] =>
  Array.isArray(documents) ? documents.map(normalizeDocument) : [];
